import { Eye, EyeOff, UserPlus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ErroApi } from '../../lib/api';
import { useSessao } from '../../lib/sessao';
import { PrimaryButton } from './primary-button';
import { TextField } from './text-field';

/** O mínimo que o `criarEscritorioSchema` aceita. Um número só, nos dois lados. */
const SENHA_MINIMA = 12;

/**
 * AUTOCADASTRO — o contador abre o próprio escritório.
 *
 * ## Quatro campos, e nem um a mais
 * Nome do escritório, nome de quem administra, e-mail e senha. Telefone, CNPJ
 * do escritório e quantidade de clientes são coisas que dá vontade de
 * perguntar aqui e que o produto não precisa para funcionar — e cada campo a
 * mais na porta de entrada é gente que desiste antes de ver o produto. O que
 * faltar, o escritório preenche depois, de dentro.
 *
 * ## A senha é conferida ANTES de sair da tela
 * O servidor recusa abaixo de doze caracteres, mas descobrir isso depois do
 * envio é uma viagem de ida e volta para dizer o que dava para dizer na hora.
 * O número vem de `SENHA_MINIMA`, aqui e no schema — dois números diferentes
 * produziriam uma tela que aceita o que o servidor recusa.
 */
export function CadastroForm(): JSX.Element {
  const criarEscritorio = useSessao((s) => s.criarEscritorio);

  const [nomeEscritorio, setNomeEscritorio] = useState('');
  const [nomeAdministrador, setNomeAdministrador] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [aceite, setAceite] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [senhaVisivel, setSenhaVisivel] = useState(false);

  const senhaCurta = senha.length > 0 && senha.length < SENHA_MINIMA;
  const pronto =
    nomeEscritorio.trim().length >= 3 &&
    nomeAdministrador.trim().length >= 3 &&
    email.length > 0 &&
    senha.length >= SENHA_MINIMA &&
    aceite;

  async function enviar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      await criarEscritorio({
        nomeEscritorio: nomeEscritorio.trim(),
        nomeAdministrador: nomeAdministrador.trim(),
        email: email.trim(),
        senha,
        aceiteTermos: true,
      });
      // Sem `navigate`: entrar troca o estado da sessão, e o roteador em
      // `App.tsx` já leva para dentro do produto sozinho.
    } catch (falha) {
      setErro(
        falha instanceof ErroApi ? falha.message : 'Não foi possível criar o escritório.',
      );
    } finally {
      setEnviando(false);
    }
  }

  return (
    <section className="bg-auth-superficie/2 relative flex flex-col justify-center p-8 sm:p-10 md:p-12">
      <form onSubmit={(e) => void enviar(e)} className="w-full">
        <h2 className="text-auth-texto text-2xl font-light tracking-tight">
          Criar meu escritório
        </h2>
        <p className="text-auth-texto-secundario mt-1.5 mb-7 text-sm">
          São 7 dias de teste, sem cartão. Você já entra usando.
        </p>

        {erro && (
          <div
            role="alert"
            aria-live="polite"
            className="bg-auth-erro/10 border-auth-erro/25 text-auth-erro mb-5 rounded-auth-controle border px-4 py-3 text-sm"
          >
            {erro}
          </div>
        )}

        <div className="mb-4">
          <TextField
            id="nomeEscritorio"
            rotulo="Nome do escritório"
            autoComplete="organization"
            value={nomeEscritorio}
            onChange={(e) => setNomeEscritorio(e.target.value)}
            required
          />
        </div>

        <div className="mb-4">
          <TextField
            id="nomeAdministrador"
            rotulo="Seu nome"
            autoComplete="name"
            value={nomeAdministrador}
            onChange={(e) => setNomeAdministrador(e.target.value)}
            required
          />
        </div>

        <div className="mb-4">
          <TextField
            id="email"
            rotulo="E-mail"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="mb-4">
          {/*
            Mesma construção do olho do login, e pelo mesmo motivo: o nome do
            botão não contém "senha", senão `getByLabel('Senha')` casaria com
            dois controles.
          */}
          <TextField
            id="senha"
            rotulo="Senha"
            type={senhaVisivel ? 'text' : 'password'}
            autoComplete="new-password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            required
            minLength={SENHA_MINIMA}
            className="pr-11"
            trailing={
              <button
                type="button"
                onClick={() => setSenhaVisivel((v) => !v)}
                aria-pressed={senhaVisivel}
                aria-label={
                  senhaVisivel
                    ? 'Ocultar o que foi digitado'
                    : 'Exibir o que foi digitado'
                }
                title={senhaVisivel ? 'Ocultar' : 'Exibir'}
                className="text-auth-texto-secundario hover:text-auth-texto focus-visible:ring-auth-marca-hover/35 absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-auth-controle transition-colors focus-visible:ring-2 focus-visible:outline-none"
              >
                {senhaVisivel ? (
                  <EyeOff className="size-[18px]" aria-hidden />
                ) : (
                  <Eye className="size-[18px]" aria-hidden />
                )}
              </button>
            }
          />
          <p
            className={
              senhaCurta
                ? 'text-auth-erro mt-1.5 text-xs'
                : 'text-auth-texto-secundario mt-1.5 text-xs'
            }
            aria-live="polite"
          >
            {senhaCurta
              ? `Faltam ${SENHA_MINIMA - senha.length} caracteres.`
              : `Mínimo de ${SENHA_MINIMA} caracteres. Comprimento protege mais que símbolo.`}
          </p>
        </div>

        {/*
          O aceite é um campo do corpo da requisição, não um enfeite: o schema
          recusa `false`. Sem isso, o aceite ficaria só na tela — e o que fica
          na tela não aconteceu.
        */}
        <label className="text-auth-texto-secundario mb-6 flex items-start gap-2.5 text-sm">
          <input
            type="checkbox"
            checked={aceite}
            onChange={(e) => setAceite(e.target.checked)}
            className="accent-auth-marca-hover mt-0.5 size-4 shrink-0"
          />
          <span>
            Li e aceito os termos de uso e a política de privacidade da Specializer.
          </span>
        </label>

        <PrimaryButton type="submit" carregando={enviando} disabled={!pronto}>
          {!enviando && <UserPlus className="size-4" aria-hidden />}
          {enviando ? 'Criando…' : 'Criar escritório e entrar'}
        </PrimaryButton>

        <p className="text-auth-texto-secundario mt-5 text-center text-sm">
          Já tem conta?{' '}
          <Link
            to="/entrar"
            className="text-auth-marca-hover hover:text-auth-acento focus-visible:ring-auth-marca-hover/35 rounded font-medium underline-offset-4 transition-colors hover:underline focus-visible:ring-2 focus-visible:outline-none"
          >
            Entrar
          </Link>
        </p>

        <div className="border-auth-borda/10 text-auth-texto-secundario mt-6 border-t pt-4 text-xs leading-relaxed">
          <strong className="text-auth-texto font-medium">Nada é cobrado agora.</strong> O
          teste dura 7 dias e não pede cartão. Ao fim dele, os contratos que você já gerou
          continuam disponíveis para leitura.
        </div>
      </form>
    </section>
  );
}
